import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import LocationStep from './components/LocationStep';
import CategoryStep from './components/CategoryStep';
import PrestationStep from './components/PrestationStep';
import AddressStep from './components/AddressStep';
import DateTimeStep from './components/DateTimeStep';
import ClientInfoStep from './components/ClientInfoStep';
import ProgressBar from './components/ProgressBar';

type ReservationData = {
  location: string;
  category: string;
  prestation: any; // replace `any` with a proper type when known
  size: string;
  price: number;
  options: string[];
  address: string;
  postalCode: string;
  city: string;
  distance: number;
  travelCost: number;
  date: string;
  time: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  vehicleType: string;
  additionalInfo: string;
};

export default function ReservationPage() {
  const [step, setStep] = useState<number>(1);
  const [reservationData, setReservationData] = useState<ReservationData>({
    location: '',
    category: '',
    prestation: null,
    size: '',
    price: 0,
    options: [],
    address: '',
    postalCode: '',
    city: '',
    distance: 0,
    travelCost: 0,
    date: '',
    time: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    vehicleType: '',
    additionalInfo: '',
  });

  /** Safely merge new data into the reservation state */
  const updateData = (newData: Partial<ReservationData>) => {
    setReservationData(prev => ({ ...prev, ...newData }));
  };

  /** Move to the next step – caps at the maximum step (6) */
  const nextStep = () => {
    setStep(prev => {
      const next = Math.min(prev + 1, 6);
      return next;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** Move to the previous step – never goes below 1 */
  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** Render the component that corresponds to the current step */
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <LocationStep
            data={reservationData}
            updateData={updateData}
            nextStep={nextStep}
          />
        );
      case 2:
        return (
          <CategoryStep
            data={reservationData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 3:
        return (
          <PrestationStep
            data={reservationData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 4:
        return (
          <AddressStep
            data={reservationData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 5:
        return (
          <DateTimeStep
            data={reservationData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 6:
        return (
          <ClientInfoStep
            data={reservationData}
            updateData={updateData}
            prevStep={prevStep}
          />
        );
      default:
        // Defensive fallback – should never happen
        return null;
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch';

  return (
    <div className="min-h-screen bg-dark-bg">
      <SeoHead
        title="Réserver une Prestation Automobile en Ligne | Louati AUTOCENTER Payerne"
        description="Réservez votre prestation d'esthétique automobile en ligne 24h/24 à Payerne. Nettoyage, polish, céramique, ciel étoilé. Paiement sécurisé. Service à domicile disponible."
        keywords="réservation detailing Payerne, prendre rendez-vous lavage auto, réserver polish voiture Suisse"
        canonical={`${siteUrl}/reservation`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Réserver une Prestation Automobile en Ligne | Louati AUTOCENTER Payerne",
          "description": "Réservez votre prestation d'esthétique automobile en ligne 24h/24 à Payerne.",
          "url": `${siteUrl}/reservation`,
          "inLanguage": "fr-CH",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
              { "@type": "ListItem", "position": 2, "name": "Réservation", "item": `${siteUrl}/reservation` }
            ]
          }
        }}
      />
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          <ProgressBar currentStep={step} />
          {renderStep()}
        </div>
      </div>

      <Footer />
    </div>
  );
}
